import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadInitialData } from '../../../../redux/slices/housingSlice';
import * as XLSX from 'xlsx';
import { createHouseDetail } from '../../../../services/api';
import './BulkUpload.scss';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { AiOutlineCloudUpload } from "react-icons/ai";

const BulkUpload = () => {
    const dispatch = useDispatch();
    const { years, categories, blocks, panchayats, villages, loading } = useSelector(state => state.housingData);
    const [fileName, setFileName] = useState('');
    const history = useHistory ();

    const [excelData, setExcelData] = useState([]);
    const [mappedData, setMappedData] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [errorRows, setErrorRows] = useState([]);

    // Load initial API data
    useEffect(() => {
        dispatch(loadInitialData());
    }, [dispatch]);

    // Map Excel data to API IDs after API data is loaded
    useEffect(() => {
        if (!loading && years.length && blocks.length && panchayats.length && villages.length && excelData.length) {
            const mapped = mapExcelDataToIds(excelData, { years, categories, blocks, panchayats, villages });
            setMappedData(mapped);
        }
    }, [loading, years, categories, blocks, panchayats, villages, excelData]);

    // Handle file upload and parse the Excel file
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (event) => {
            const binaryStr = event.target.result;
            const workbook = XLSX.read(binaryStr, { type: 'binary' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsedData = XLSX.utils.sheet_to_json(worksheet);
            setExcelData(parsedData);
        };
        reader.readAsBinaryString(file);
    };

    // Handle file drag-and-drop
    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        handleFileChange({ target: { files: [file] } });
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleUpload = async () => {
        if (mappedData.length === 0) {
            alert('No data to submit');
            return;
          }
      
          let newErrorRows = [];
          console.log(mappedData);
          
          for (const row of mappedData) {
            if (!row.yearId || !row.blockId || !row.panchayatId || !row.villageId || !row.categoryId) {
              newErrorRows.push(row);
              continue;
            }
            try {
              await createHouseDetail(row);
            } catch (error) {
              newErrorRows.push(row);
              console.error('Error uploading data:', error);
            }
          }
          setErrorRows(newErrorRows);
       
        setUploading(false);
        setSelectedFile(null);
        setExcelData([]);
          if (newErrorRows.length > 0) {
            setErrorRows(newErrorRows);
      
          } else {
            alert('Data successfully uploaded');
            setExcelData([]);
            setFileName('');
            handleCancel();
            history.goBack();

          }

    };

    const handleCancel = () => {
        setSelectedFile(null);
        setExcelData([]);
        setMappedData([]);
    };

    const handleDownloadErrorFile = () => {
        exportErrorRowsToExcel(errorRows);
        history.goBack();
      };
      const exportErrorRowsToExcel = (errorRows) => {
        const worksheet = XLSX.utils.json_to_sheet(errorRows.map(row => ({
            ...row,
            Errors: (row.errors || []).join(", ") // Default to an empty array if errors is undefined
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
        XLSX.writeFile(workbook, "error_upload.xlsx");
    };
    
    return (
        <div className='bulkupload'>
            <div className='bulkupload-container'>
                <div className='bulkupload-head'>
                    <h2>Bulk Upload</h2>
                    <a className='save-btn' href="/BulkUpload.xlsx" download>Download Template</a>
                </div>

                <div className='bulkupload-content'>
                    <div onDragOver={handleDragOver} onDrop={handleDrop} className='drop-box'>
                        <input
                            id="fileInput"
                            type="file"
                            accept=".xls,.xlsx"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="fileInput">
                            {selectedFile ? (
                                <div className='upload-box'>
                                    <p>{selectedFile.name}</p>
                                    <div className='details-button'>
                                        <button className='save-btn' onClick={handleUpload} disabled={uploading}>
                                            {uploading ? 'Uploading...' : 'Upload'}
                                        </button>
                                        <button className='close-btn' onClick={handleCancel}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className='pre'>
                                    <AiOutlineCloudUpload />

                                    <p>Drag & drop files here or</p>
                                    <button onClick={() => document.getElementById('fileInput').click()}>Browse</button>
                                </div>
                            )}
                        </label>
                    </div>

                    {excelData.length > 0 && (
                        <div className='excel-data table-container'>
                            <h3>Excel Data:</h3>
                            <table className='custom-table'>
                                <thead>
                                    <tr>
                                        {Object.keys(excelData[0]).map((key) => (
                                            key !== '__rowNum__' && <th key={key}>{key}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {excelData.map((row, index) => (
                                        <tr key={index}>
                                            {Object.keys(excelData[0]).map((key) => (
                                                key !== '__rowNum__' && (
                                                    <td key={key}>
                                                        {row[key] !== undefined ? row[key] : 'N/A'}
                                                    </td>
                                                )
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {errorRows.length > 0 && (
                    <div className='error-rows p-15'>
                        <p>There were errors in some rows. Please download the error file for more details.</p>
                        <button className='btn' onClick={handleDownloadErrorFile}>
                            Download Error File
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUpload;

const mapExcelDataToIds = (excelData, apiData) => {
    const { years, categories, blocks, panchayats, villages } = apiData;

    return excelData.map(row => ({
        yearId: years.find(year => year.name === row.year)?.id,
        blockId: blocks.find(block => block.name === row.blockName)?.id,
        panchayatId: panchayats.find(p => p.name === row.panchayat)?.id,
        villageId: villages.find(v => v.name === row.village)?.id,
        regNo: row.regNo,
        beneficiary: row.beneficiary,
        fatherorHusband: row.fatherorHusband,
        sanctionNo: row.sanctionNo,
        categoryId: categories.find(cat => cat.name === row.category)?.id,
        releasedAmount: null,
        installment: null,
        creditDate: null,
        houseStatusId: null,
        activeStatus: true,
    }));
};
